// @ts-nocheck
/* Valtech Sync - RfqQuoteDetailsHeader */

import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getQuoteState } from "@insite/client-framework/Store/Data/Quotes/QuotesSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import acceptJobQuote from "@insite/client-framework/Store/Pages/RfqQuoteDetails/Handlers/AcceptJobQuote";
import declineQuote from "@insite/client-framework/Store/Pages/RfqQuoteDetails/Handlers/DeclineQuote";
import deleteQuote from "@insite/client-framework/Store/Pages/RfqQuoteDetails/Handlers/DeleteQuote";
import submitQuote from "@insite/client-framework/Store/Pages/RfqQuoteDetails/Handlers/SubmitQuote";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import TwoButtonModal, { TwoButtonModalStyles } from "@insite/content-library/Components/TwoButtonModal";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { LinkPresentationProps } from "@insite/mobius/Link";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import OrderActionSet, {
    orderActionSetFieldDefinitions,
    OrderActionSetPresentationProps,
} from "@store/Library/Components/Toolkit/Order/OrderActionSet";
import ga from "@toolkit/Framework/Common/GoogleAnalytics";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import TrashFill from "@toolkit/Library/Components/Icons/Trash/TrashFill";
import React, { useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    showAccept = "showAccept",
    showDecline = "showDecline",
    showDelete = "showDelete",
    showPrint = "showPrint",
    showSubmit = "showSubmit",
}

interface OwnProps extends WidgetProps, ExpandStyles<RfqQuoteDetailsActionsStyles> {
    fields: {
        [fields.showAccept]: boolean;
        [fields.showDecline]: boolean;
        [fields.showDelete]: boolean;
        [fields.showPrint]: boolean;
        [fields.showSubmit]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    cartState: getCurrentCartState(state),
    checkoutShippingPageUrl: getPageLinkByPageType(state, "CheckoutShippingPage")?.url,
    quoteState: getQuoteState(state, state.pages.rfqQuoteDetails.quoteId),
    rfqJobQuotesPageUrl: getPageLinkByPageType(state, "RfqJobQuotesPage")?.url,
    rfqMyQuotesPageUrl: getPageLinkByPageType(state, "RfqMyQuotesPage")?.url,
});

const mapDispatchToProps = {
    acceptJobQuote,
    declineQuote,
    deleteQuote,
    submitQuote,
};

export interface RfqQuoteDetailsActionsStyles {
    actionSet?: OrderActionSetPresentationProps;
    deleteQuoteButton?: ButtonPresentationProps;
    submitQuoteButton?: ButtonPresentationProps;
    declineQuoteButton?: ButtonPresentationProps;
    acceptQuoteButton?: ButtonPresentationProps;
    printButton?: ButtonPresentationProps;
    printClickable?: LinkPresentationProps;
    deleteQuoteClickable?: LinkPresentationProps;
    submitQuoteClickable?: LinkPresentationProps;
    declineQuoteClickable?: LinkPresentationProps;
    acceptQuoteClickable?: LinkPresentationProps;
    deleteQuoteModal?: TwoButtonModalStyles;
    cartNotificationModal?: TwoButtonModalStyles;
}

export const rfqQuoteDetailsActionsStyles: RfqQuoteDetailsActionsStyles = {
    deleteQuoteButton: { variant: "secondary" },
    deleteQuoteClickable: { icon: { iconProps: { src: TrashFill } } },
    declineQuoteButton: { variant: "secondary" },
};

type Props = OwnProps & HasHistory & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const RfqQuoteDetailsActions = ({
    acceptJobQuote,
    cartState,
    checkoutShippingPageUrl,
    declineQuote,
    deleteQuote,
    fields: widgetFields,
    history,
    quoteState,
    rfqJobQuotesPageUrl,
    rfqMyQuotesPageUrl,
    submitQuote,
    extendedStyles,
    overridedStyles,
}: Props) => {
    const styles = useExpandProps(rfqQuoteDetailsActionsStyles, extendedStyles, overridedStyles);

    const toasterContext = useContext(ToasterContext);
    const [deleteQuoteModalIsOpen, setDeleteQuoteModalIsOpen] = useState(false);
    const [cartNotificationModalIsOpen, setCartNotificationModalIsOpen] = useState(false);

    /* Valtech Custom Code - Start */
    const trackQuoteAccepted = ga.use.trackQuoteAccepted();
    const trackQuoteDeleted = ga.use.trackQuoteDeleted();
    const trackQuoteRejected = ga.use.trackQuoteRejected();
    /* Valtech Custom Code - End */

    if (!quoteState.value) {
        return null;
    }

    const quote = quoteState.value;

    const deleteQuoteClickHandler = () => {
        setDeleteQuoteModalIsOpen(true);
    };

    const submitDeleteQuoteHandler = () => {
        deleteQuote({
            quoteId: quote.id,
            onSuccess: () => {
                trackQuoteDeleted(); // Valtech Custom Code
                toasterContext.addToast({ body: translate("Quote deleted."), messageType: "success" });
                redirectToMyQuotes();
            },
            onComplete() {
                // "this" is targeting the object being created, not the parent SFC
                // eslint-disable-next-line react/no-this-in-sfc
                this.onSuccess?.();
            },
        });
    };

    const cancelDeleteQuoteHandler = () => {
        setDeleteQuoteModalIsOpen(false);
    };

    const submitCartNotificationHandler = () => {
        trackQuoteAccepted(); // Valtech Custom Code
        redirectToCheckout();
    };

    const cancelCartNotificationHandler = () => {
        setCartNotificationModalIsOpen(false);
    };

    const submitQuoteClickHandler = () => {
        submitQuote({
            quote,
            onSuccess: () => {
                toasterContext.addToast({ body: translate("Quote submitted."), messageType: "success" });
                redirectToMyQuotes();
            },
            onComplete(resultProps) {
                if (resultProps.apiResult) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onSuccess?.();
                }
            },
        });
    };

    const declineQuoteClickHandler = () => {
        declineQuote({
            quote,
            onSuccess: () => {
                trackQuoteRejected(); // Valtech Custom Code
                toasterContext.addToast({ body: translate("Quote declined."), messageType: "success" });
                redirectToMyQuotes();
            },
            onComplete(resultProps) {
                if (resultProps.apiResult) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onSuccess?.();
                }
            },
        });
    };

    const acceptQuoteClickHandler = () => {
        if (quote.isJobQuote) {
            acceptJobQuote({
                quote,
                onSuccess: () => {
                    trackQuoteAccepted(); // Valtech Custom Code
                    toasterContext.addToast({ body: translate("Quote accepted."), messageType: "success" });
                    redirectToJobQuotes();
                },
                onComplete(resultProps) {
                    if (resultProps.apiResult) {
                        // "this" is targeting the object being created, not the parent SFC
                        // eslint-disable-next-line react/no-this-in-sfc
                        this.onSuccess?.();
                    }
                },
            });
        } else if (cartState.value?.lineCount === 0) {
            trackQuoteAccepted(); // Valtech Custom Code
            redirectToCheckout();
        } else {
            setCartNotificationModalIsOpen(true);
        }
    };

    const redirectToMyQuotes = () => {
        if (rfqMyQuotesPageUrl) {
            history.push(rfqMyQuotesPageUrl);
        }
    };

    const redirectToCheckout = () => {
        if (checkoutShippingPageUrl) {
            history.push(`${checkoutShippingPageUrl}?cartId=${quote.id}`);
        }
    };

    const redirectToJobQuotes = () => {
        if (rfqJobQuotesPageUrl) {
            history.push(rfqJobQuotesPageUrl);
        }
    };

    const expirationDateIsGreaterThanCurrentDate = () => {
        if (!quote || !quote.expirationDate) {
            return true;
        }

        const expirationDate = new Date(quote.expirationDate.toString());
        const currentDate = new Date();

        expirationDate.setHours(0, 0, 0, 0);
        currentDate.setHours(0, 0, 0, 0);

        return expirationDate >= currentDate;
    };

    const quoteType = quote.isJobQuote ? "Job Quote" : "Sales Quote";
    const deleteQuoteLabel = translate(`Delete ${quoteType}`);
    const submitQuoteLabel = translate(`Submit ${quoteType}`);
    const declineQuoteLabel = translate(`Decline ${quoteType}`);
    const acceptQuoteLabel = translate(`Accept ${quoteType}`);
    const canBeSubmittedOrDeleted = quote.isSalesperson && quote.isEditable;
    const expirationDateIsValid = expirationDateIsGreaterThanCurrentDate();
    const canBeDeclined = !quote.isSalesperson && quote.status === "QuoteProposed" && expirationDateIsValid;
    const canBeAccepted =
        !quote.failedToGetRealTimeInventory &&
        !quote.isSalesperson &&
        (quote.status === "QuoteProposed" || quote.status === "AwaitingApproval") &&
        expirationDateIsValid;

    return (
        <>
            <OrderActionSet
                {...styles.actionSet}
                fields={widgetFields}
                actions={[
                    {
                        key: "delete",
                        label: deleteQuoteLabel,
                        onClick: deleteQuoteClickHandler,
                        buttonStyles: styles.deleteQuoteButton,
                        linkStyles: styles.deleteQuoteClickable,
                        isVisible: canBeSubmittedOrDeleted,
                        field: fields.showDelete,
                    },
                    {
                        key: "submit",
                        label: submitQuoteLabel,
                        onClick: submitQuoteClickHandler,
                        buttonStyles: styles.submitQuoteButton,
                        linkStyles: styles.submitQuoteClickable,
                        isVisible: canBeSubmittedOrDeleted,
                        field: fields.showSubmit,
                    },
                    {
                        key: "decline",
                        label: declineQuoteLabel,
                        onClick: () => declineQuoteClickHandler(),
                        buttonStyles: styles.declineQuoteButton,
                        linkStyles: styles.declineQuoteClickable,
                        isVisible: canBeDeclined,
                        field: fields.showDecline,
                    },
                    {
                        key: "accept",
                        label: acceptQuoteLabel,
                        onClick: () => acceptQuoteClickHandler(),
                        buttonStyles: styles.acceptQuoteButton,
                        linkStyles: styles.acceptQuoteClickable,
                        isVisible: canBeAccepted,
                        field: fields.showAccept,
                    },
                    {
                        preset: "print",
                        buttonStyles: styles.printButton,
                        linkStyles: styles.printClickable,
                        field: fields.showPrint,
                    },
                ]}
            />
            <TwoButtonModal
                {...styles.deleteQuoteModal}
                modalIsOpen={deleteQuoteModalIsOpen}
                headlineText={translate("Delete Quote")}
                messageText={siteMessage("Rfq_DeleteQuoteConfirmation")}
                cancelButtonText={translate("Cancel")}
                submitButtonText={translate("Delete")}
                onCancel={cancelDeleteQuoteHandler}
                onSubmit={submitDeleteQuoteHandler}
                submitTestSelector="submitDeleteQuote"
            />
            <TwoButtonModal
                {...styles.cartNotificationModal}
                modalIsOpen={cartNotificationModalIsOpen}
                headlineText={translate("Cart Notification")}
                messageText={siteMessage("Rfq_CartNotificationMessage")}
                cancelButtonText={translate("Cancel")}
                submitButtonText={translate("Continue")}
                onCancel={cancelCartNotificationHandler}
                onSubmit={submitCartNotificationHandler}
                submitTestSelector="submitCartNotification"
            />
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(RfqQuoteDetailsActions)),
    definition: {
        group: "RFQ Quote Details",
        allowedContexts: ["RfqQuoteDetailsPage"],
        displayName: "Actions",
        fieldDefinitions: orderActionSetFieldDefinitions([
            { name: fields.showPrint },
            { name: fields.showAccept, defaultState: "sticky" },
            { name: fields.showDecline, defaultState: "sticky" },
            { name: fields.showDelete, defaultState: "sticky" },
            { name: fields.showSubmit, defaultState: "sticky" },
        ]),
    },
};

export default widgetModule;
